import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import styles from './ui.module.css';

export interface ToastOptions {
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss. */
  duration?: number;
}

interface ActiveToast extends ToastOptions {
  id: number;
}

const ToastContext = createContext<((options: ToastOptions) => void) | null>(
  null
);

/**
 * The success toast the auth pages raise after a background request. Only the
 * solid/success variant exists because that is the only one the portal ever
 * used; anything else should be an inline message, not an overlay.
 */
function SuccessIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden="true"
      width="20"
      height="24"
      style={{ display: 'block' }}
    >
      <path
        fill="currentColor"
        d="M12,0A12,12,0,1,0,24,12,12.014,12.014,0,0,0,12,0Zm6.927,8.2-6.845,9.289a1.011,1.011,0,0,1-1.43.188L5.764,13.769a1,1,0,1,1,1.25-1.562l4.076,3.261,6.227-8.451A1,1,0,1,1,18.927,8.2Z"
      />
    </svg>
  );
}

function Toast({ toast, onDone }: { toast: ActiveToast; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, toast.duration ?? 6000);
    return () => clearTimeout(timer);
  }, [toast.duration, onDone]);

  return (
    <div className={styles.toast} role="status" aria-live="polite">
      <span className={styles.toastIcon}>
        <SuccessIcon />
      </span>
      <div className={styles.toastBody}>
        <p className={styles.toastTitle}>{toast.title}</p>
        {toast.description && (
          <p className={styles.toastDesc}>{toast.description}</p>
        )}
      </div>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  // A module-level counter would leak between tests; a ref-free closure over
  // the current list is enough since ids only need to be unique while mounted.
  const push = useCallback((options: ToastOptions) => {
    setToasts((current) => [
      ...current,
      { ...options, id: (current.at(-1)?.id ?? 0) + 1 },
    ]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        toasts.length > 0 &&
        createPortal(
          <div className={styles.toastViewport}>
            {toasts.map((toast) => (
              <Toast
                key={toast.id}
                toast={toast}
                onDone={() => dismiss(toast.id)}
              />
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}

/** No-ops when no provider is mounted, matching Chakra's behaviour in tests. */
export function useToast() {
  const push = useContext(ToastContext);
  return useCallback(
    (options: ToastOptions) => {
      push?.(options);
    },
    [push]
  );
}
